import type React from "react"

import { useEffect, useState } from "react"
import axios from "axios"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Building2, PlusCircle, CheckCircle, XCircle } from "lucide-react"
import { FullBrandData, IBrand } from "@/interfaces"
import PlatformModal from "@/components/dashboard_component/PlatformModal"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "@/store"
import { setBrands } from "@/store/slices/BrandSlice"
import { baseURL } from "@/data/constant"


interface PlatformIcon {
    icon: React.ElementType;
    name: string;
}

interface BrandIntegrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    brandId: string;
    brandName: string;
    brandData: FullBrandData;
    onBrandUpdate?: (brand: FullBrandData) => void;
    platformIcons: {
        shopify: PlatformIcon;
        googleAds: PlatformIcon;
        googleAnalytics: PlatformIcon;
        facebook: PlatformIcon;
        instagram: PlatformIcon;
    };
    initialPlatform?: string | null;
}

export function BrandIntegrationModal({
    isOpen,
    onClose,
    brandId,
    brandName,
    brandData,
    onBrandUpdate,
    platformIcons,
    initialPlatform = null,
}: BrandIntegrationModalProps) {
    const [activeTab, setActiveTab] = useState("overview")
    const [platformModalOpen, setPlatformModalOpen] = useState(false)
    const [selectedPlatform, setSelectedPlatform] = useState("")
    const [disconnectingPlatform, setDisconnectingPlatform] = useState<string | null>(null)
    const [localBrandData, setLocalBrandData] = useState<FullBrandData>(brandData)
    const dispatch = useDispatch()
    const brands = useSelector((state: RootState) => state.brand.brands)

    useEffect(() => {
        setLocalBrandData(brandData)
    }, [brandData])

    // After OAuth redirect, auto-open the platform account picker
    useEffect(() => {
        if (!initialPlatform) return
        setActiveTab(initialPlatform)
        setSelectedPlatform(initialPlatform)
        setPlatformModalOpen(true)
    }, [initialPlatform])

    const platforms = [
        {
            key: "shopify",
            icon: platformIcons.shopify.icon,
            name: platformIcons.shopify.name,
            isConnected: !!localBrandData.shopifyAccount?.shopName,
            accountNumber: '1 Store',
            accountName: localBrandData.shopifyAccount?.shopName,
            data: localBrandData.shopifyAccount,
            allowMultipleAccounts: false
        },
        {
            key: "googleAds",
            icon: platformIcons.googleAds.icon,
            name: platformIcons.googleAds.name,
            isConnected: !!(localBrandData.googleAdAccount && localBrandData.googleAdAccount.length > 0),
            accountNumber: localBrandData.googleAdAccount ? `${localBrandData.googleAdAccount.length} Account(s)` : undefined,
            accountName: localBrandData.googleAdAccount && localBrandData.googleAdAccount.length > 0
            ? `${localBrandData.googleAdAccount.map((acc) => acc.clientId).join(", ")}` 
            : undefined,
            data: localBrandData.googleAdAccount,
            allowMultipleAccounts: true
        },
        {
            key: "googleAnalytics",
            icon: platformIcons.googleAnalytics.icon,
            name: platformIcons.googleAnalytics.name,
            isConnected: !!localBrandData.ga4Account?.PropertyID,
            accountNumber: '1 Property',
            accountName: localBrandData.ga4Account?.PropertyID ? `Property ${localBrandData.ga4Account.PropertyID}` : undefined,
            data: localBrandData.ga4Account,
            allowMultipleAccounts: false
        },
        {
            key: "facebook",
            icon: platformIcons.facebook.icon,
            name: platformIcons.facebook.name,
            isConnected: !!(localBrandData.fbAdAccounts && localBrandData.fbAdAccounts.length > 0),
            accountNumber: localBrandData.fbAdAccounts ? `${localBrandData.fbAdAccounts.length} Account(s)` : undefined,
            accountName: localBrandData.fbAdAccounts && localBrandData.fbAdAccounts.length > 0
            ? `${localBrandData.fbAdAccounts.join(", ")}` 
            : undefined,
            data: localBrandData.fbAdAccounts,
            allowMultipleAccounts: true
        },
        {
            key: "instagram",
            icon: platformIcons.instagram.icon,
            name: platformIcons.instagram.name,
            isConnected: !!(localBrandData.igAccountIds && localBrandData.igAccountIds.length > 0),
            accountNumber: localBrandData.igAccountIds ? `${localBrandData.igAccountIds.length} Account(s)` : undefined,
            accountName: localBrandData.igAccountIds && localBrandData.igAccountIds.length > 0
            ? `${localBrandData.igAccountIds.join(", ")}` 
            : undefined,
            data: localBrandData.igAccountIds,
            allowMultipleAccounts: true
        }
    ]

    const handleDisconnect = async (platformKey: string) => {
        setDisconnectingPlatform(platformKey)
        try {
            let payload: any = {
                platform: platformKey === 'googleAds' ? 'google ads'
                    : platformKey === 'googleAnalytics' ? 'google analytics'
                    : platformKey
            }

            // Omit accountId for facebook/googleAds/instagram so the API disconnects all accounts
            switch (platformKey) {
                case 'facebook':
                case 'instagram':
                case 'googleAds':
                    break
                case 'googleAnalytics':
                    break
                case 'shopify':
                    payload.shopName = localBrandData.shopifyAccount?.shopName
                    break
            }

            const response = await axios.patch(
                `${baseURL}/api/brands/platform/${brandId}`,
                payload,
                { withCredentials: true }
            )

            // Clear cache for this platform
            try {
                if (platformKey === 'facebook') {
                    await axios.delete(
                        `${baseURL}/api/setup/fb-ad-accounts-cache/${brandId}`,
                        { withCredentials: true }
                    )
                } else if (platformKey === 'instagram') {
                    await axios.delete(
                        `${baseURL}/api/setup/ig-accounts-cache/${brandId}`,
                        { withCredentials: true }
                    )
                }
            } catch (cacheError) {
                console.warn('Cache clear failed, but continuing:', cacheError)
            }

            // Update local + parent + Redux state so the modal refreshes immediately
            if (response.data.brand) {
                const updatedBrand = response.data.brand as FullBrandData
                setLocalBrandData(updatedBrand)
                onBrandUpdate?.(updatedBrand)

                const updatedBrands: IBrand[] = brands.map((b) => {
                    if (b._id !== brandId) return b

                    const next: IBrand = { ...b }

                    if (platformKey === 'facebook') {
                        next.fbAdAccounts = (updatedBrand.fbAdAccounts || []) as []
                    } else if (platformKey === 'instagram') {
                        next.igAccountIds = (updatedBrand.igAccountIds || []) as []
                    } else if (platformKey === 'googleAds') {
                        next.googleAdAccount = updatedBrand.googleAdAccount
                    } else if (platformKey === 'googleAnalytics') {
                        next.ga4Account = updatedBrand.ga4Account
                            ? { PropertyID: updatedBrand.ga4Account.PropertyID }
                            : {}
                    } else if (platformKey === 'shopify') {
                        next.shopifyAccount = updatedBrand.shopifyAccount?.shopName
                            ? {
                                shopName: updatedBrand.shopifyAccount.shopName,
                                shopId: String(updatedBrand.shopifyAccount.shopId ?? ''),
                              }
                            : {}
                    }

                    return next
                })
                dispatch(setBrands(updatedBrands))
            }

            // Reset to overview tab
            setActiveTab("overview")
        } catch (error) {
            console.error(`Error disconnecting ${platformKey}:`, error)
            alert(`Failed to disconnect ${platformKey}. Please try again.`)
        } finally {
            setDisconnectingPlatform(null)
        }
    }

    const handleAddAccount = (platformKey: string) => {
        setSelectedPlatform(platformKey)
        setPlatformModalOpen(true)
    }

    const refreshBrandData = async () => {
        try {
            const response = await axios.get(`${baseURL}/api/brands/${brandId}`, {
                withCredentials: true,
            })
            const updatedBrand = response.data as FullBrandData
            setLocalBrandData(updatedBrand)
            onBrandUpdate?.(updatedBrand)

            const updatedBrands: IBrand[] = brands.map((b) => {
                if (b._id !== brandId) return b
                return {
                    ...b,
                    fbAdAccounts: (updatedBrand.fbAdAccounts || []) as [],
                    igAccountIds: (updatedBrand.igAccountIds || []) as [],
                    googleAdAccount: updatedBrand.googleAdAccount,
                    ga4Account: updatedBrand.ga4Account?.PropertyID
                        ? { PropertyID: updatedBrand.ga4Account.PropertyID }
                        : {},
                    shopifyAccount: updatedBrand.shopifyAccount?.shopName
                        ? {
                            shopName: updatedBrand.shopifyAccount.shopName,
                            shopId: String(updatedBrand.shopifyAccount.shopId ?? ''),
                          }
                        : {},
                }
            })
            dispatch(setBrands(updatedBrands))
            return updatedBrand
        } catch (error) {
            console.error('Error refreshing brand after connect:', error)
            return null
        }
    }
    
    const handlePlatformModalSuccess = async (_platform: string, _accountName: string, _accountId: string) => {
        await refreshBrandData()
        setPlatformModalOpen(false)
        setSelectedPlatform("")
    }

    const handlePlatformModalOpenChange = async (open: boolean) => {
        setPlatformModalOpen(open)
        if (!open) {
            // Re-fetch after close so reconnect/OAuth + account select always reflects in UI
            await refreshBrandData()
            setSelectedPlatform("")
        }
    }
    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-4xl max-h-[100vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-teal-600" />
                        </div>
                        <DialogTitle className="text-xl">{brandName}</DialogTitle>
                    </div>
                    <DialogDescription>Manage platform integrations for this brand</DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="mt-2">
                    <TabsList className="grid grid-cols-6 mb-4 h-auto">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="shopify">Shopify</TabsTrigger>
                        <TabsTrigger value="googleAds">Google Ads</TabsTrigger>
                        <TabsTrigger value="googleAnalytics">Analytics</TabsTrigger>
                        <TabsTrigger value="facebook">Facebook</TabsTrigger>
                        <TabsTrigger value="instagram">Instagram</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {platforms.map((platform) => (
                                <div key={platform.key} className="border rounded-lg p-4 bg-white">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                                                <platform.icon className="h-4 w-4 text-slate-700" />
                                            </div>
                                            <h3 className="font-medium">{platform.name}</h3>
                                        </div>
                                        <Badge
                                            className={platform.isConnected ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}
                                        >
                                            {platform.isConnected ? "Connected" : "Not Connected"}
                                        </Badge>
                                    </div>

                                    <div className="text-sm text-slate-600 space-y-2">
                                        {platform.isConnected ? (
                                            <>
                                                {platform.accountNumber && (
                                                    <div className="flex justify-between">
                                                        <span>Account(s):</span>
                                                        <span className="font-medium">{platform.accountNumber}</span>
                                                    </div>
                                                )}
                                                <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => setActiveTab(platform.key)}>
                                                    View Details
                                                </Button>
                                            </>
                                        ) : (
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                className="w-full"
                                                onClick={() => handleAddAccount(platform.key)}
                                            >
                                                <PlusCircle className="h-4 w-4 mr-2" />
                                                Add {platform.key === 'shopify' ? 'Store' : 'Account'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {platforms.map((platform) => (
                        <TabsContent key={platform.key} value={platform.key} className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                                        <platform.icon className="h-5 w-5 text-slate-700" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-lg">{platform.name}</h3>
                                        <span className="text-sm text-slate-500">
                                            {platform.isConnected ? `Connected to ${platform.accountName}` : "Not connected"}
                                        </span>
                                    </div>
                                </div>
                                <Badge className={platform.isConnected ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                                    {platform.isConnected ? "Active" : "Inactive"}
                                </Badge>
                            </div>

                            <div className="border rounded-lg p-5 bg-slate-50">
                                <h4 className="font-medium mb-3">Connection Details</h4>

                                {platform.isConnected ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div className="space-y-1">
                                                <p className="text-slate-500">Account Name</p>
                                                <p className="font-medium">{platform.accountName || "N/A"}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-slate-500">Status</p>
                                                <div className="flex items-center gap-1 text-green-600">
                                                    <CheckCircle className="h-4 w-4" />
                                                    <span>Connected</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 mt-4 justify-end">
                                            {platform.allowMultipleAccounts && (<Button size={"sm"} onClick={() => handleAddAccount(platform.key)}>
                                                <PlusCircle className="h-4 w-4 mr-1" />
                                                Add {platform.key === 'shopify' ? 'Store' : 'Account'}
                                            </Button>)}
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className="text-white hover:bg-red-800"
                                                onClick={() => handleDisconnect(platform.key)}
                                                disabled={disconnectingPlatform === platform.key}
                                            >
                                                {disconnectingPlatform === platform.key ? 'Disconnecting...' : 'Disconnect'}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 text-amber-600">
                                            <XCircle className="h-5 w-5" />
                                            <span>Not connected</span>
                                        </div>

                                        <div className="bg-white p-4 rounded border text-sm">
                                            <h5 className="font-medium mb-2">Why connect {platform.name}?</h5>
                                            <ul className="list-disc pl-5 space-y-1 text-slate-600">
                                                <li>Get comprehensive analytics across all your marketing channels</li>
                                                <li>Track performance metrics in one dashboard</li>
                                                <li>Generate insights from combined data sources</li>
                                            </ul>
                                        </div>

                                        <Button className="mt-2" onClick={() => handleAddAccount(platform.key)}>
                                            <PlusCircle className="h-4 w-4 mr-2" />
                                            Add {platform.key === 'shopify' ? 'Store' : 'Account'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </DialogContent>
            
            {/* Platform Modal for adding accounts */}
            {selectedPlatform && (
                <PlatformModal
                    platform={selectedPlatform === 'googleAds' ? 'Google Ads' : 
                              selectedPlatform === 'googleAnalytics' ? 'Google Analytics' : 
                              selectedPlatform === 'facebook' ? 'Facebook' : 
                              selectedPlatform === 'instagram' ? 'Instagram' : 'Shopify'}
                    open={platformModalOpen}
                    onOpenChange={handlePlatformModalOpenChange}
                    brandId={brandId}
                    onSuccess={handlePlatformModalSuccess}
                />
            )}
        </Dialog>
    )
}